import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { blogPosts } from '../../mocks/blogPosts';
import BlogNavbar from './components/BlogNavbar';
import BlogFooter from './components/BlogFooter';
import PageHero from '../../components/feature/PageHero';
import SEOHead from '../../components/feature/SEOHead';

const categories = ['Todos', 'Gestão de Riscos', 'Economia Financeira', 'Seguros Empresariais', 'Seguro Automóvel', 'Gestão de Contratos'];

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState('Todos');
  const navigate = useNavigate();

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Blog IBX Brasil - Seguros Empresariais",
    "description": "Artigos, dicas e insights sobre seguros empresariais, gestão de riscos e proteção patrimonial corporativa.",
    "url": "https://ibxbrasil.com.br/blog",
    "publisher": {
      "@type": "Organization",
      "name": "IBX Brasil",
      "logo": {
        "@type": "ImageObject",
        "url": "https://ibxbrasil.com.br/logo.png"
      }
    }
  };

  const filtered = activeCategory === 'Todos'
    ? blogPosts
    : blogPosts.filter(p => p.category === activeCategory);

  return (
    <>
      <SEOHead
        title="Blog - Seguros Empresariais e Gestão de Riscos | IBX Brasil"
        description="Artigos especializados sobre seguros empresariais, gestão de riscos, proteção patrimonial e dicas para otimizar a segurança da sua empresa."
        canonical="https://ibxbrasil.com.br/blog"
        ogImage="https://readdy.ai/api/search-image?query=professional%20business%20insurance%20blog%20with%20articles%20about%20corporate%20risk%20management%20and%20enterprise%20protection%20strategies&width=1200&height=630&seq=ibx-blog-og&orientation=landscape"
        schema={blogSchema}
      />
      <div className="min-h-screen bg-white">
        <BlogNavbar />

        {/* Hero */}
        <PageHero
          badge="CONHECIMENTO QUE PROTEGE"
          title="Artigos sobre Gestão de Riscos e Contratação de **Seguros**"
          subtitle="Conteúdo especializado para ajudar empresas e pessoas a contratar seguros com mais segurança, economia e inteligência."
          backgroundImage="https://readdy.ai/api/search-image?query=modern%20professional%20business%20blog%20knowledge%20library%20insurance%20consulting%20elegant%20office%20interior%20books%20documents%20research%20deep%20teal%20petrol%20blue%20accents%20natural%20lighting%20sophisticated%20corporate%20environment&width=1920&height=1080&seq=ibxblog-hero&orientation=landscape"
          primaryCta={{
            text: "Explorar Artigos",
            action: () => {
              document.querySelector('.py-10.bg-stone-50')?.scrollIntoView({ behavior: 'smooth' });
            }
          }}
          secondaryCta={{
            text: "Falar no WhatsApp",
            href: "https://wa.me/5577988080505?text=Ol%C3%A1%2C%20gostaria%20de%20falar%20sobre%20gest%C3%A3o%20de%20seguros."
          }}
          overlayIntensity="medium"
        />

        {/* Filter */}
        <section className="py-10 bg-stone-50 border-b border-gray-100 sticky top-0 z-30 shadow-sm">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-wrap gap-2 justify-center">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat
                      ? 'bg-orange-500 text-white shadow-md'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-orange-300 hover:text-orange-500'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Posts Grid */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            {filtered.length === 0 ? (
              <div className="text-center py-24 text-gray-400">
                <i className="ri-article-line text-6xl mb-4 block"></i>
                <p className="text-base">Nenhum artigo encontrado nesta categoria.</p>
              </div>
            ) : (
              <>
                {/* Featured */}
                <div
                  className="group grid lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden shadow-lg mb-12 cursor-pointer hover:shadow-xl transition-shadow duration-300"
                  onClick={() => navigate(`/blog/${filtered[0].slug}`)}
                >
                  <div className="relative overflow-hidden h-72 lg:h-auto bg-stone-200">
                    <img
                      src={filtered[0].image}
                      alt={filtered[0].title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                      width="800"
                      height="480"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent"></div>
                    <span className="absolute top-4 left-4 bg-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Destaque
                    </span>
                  </div>
                  <div className="bg-white p-10 lg:p-14 flex flex-col justify-center">
                    <span className="inline-block text-orange-500 text-xs font-semibold uppercase tracking-widest mb-3">
                      {filtered[0].category}
                    </span>
                    <div className="flex items-center gap-4 text-xs text-gray-400 mb-4">
                      <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {filtered[0].date}</span>
                      <span className="flex items-center gap-1"><i className="ri-time-line"></i> {filtered[0].readTime} de leitura</span>
                    </div>
                    <h2
                      className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4 leading-snug group-hover:text-orange-500 transition-colors"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {filtered[0].title}
                    </h2>
                    <p className="text-sm text-gray-600 leading-relaxed mb-6">{filtered[0].excerpt}</p>
                    <a
                      href={`/blog/${filtered[0].slug}`}
                      className="inline-flex items-center justify-center px-6 py-3 bg-action-orange-500 text-white font-semibold rounded-lg hover:bg-action-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
                    >
                      Continuar Leitura
                      <i className="ri-arrow-right-line ml-2"></i>
                    </a>
                  </div>
                </div>

                {/* Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filtered.slice(1).map(post => (
                    <article
                      key={post.id}
                      className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer border border-gray-100 hover:border-orange-100"
                      onClick={() => navigate(`/blog/${post.slug}`)}
                    >
                      <div className="relative overflow-hidden h-52 bg-stone-200">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                          decoding="async"
                          width="800"
                          height="480"
                        />
                        <span className="absolute top-3 left-3 bg-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                          {post.category}
                        </span>
                      </div>
                      <div className="p-6">
                        <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
                          <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {post.date}</span>
                          <span className="flex items-center gap-1"><i className="ri-time-line"></i> {post.readTime}</span>
                        </div>
                        <h3
                          className="text-base font-bold text-gray-900 mb-3 leading-snug group-hover:text-orange-500 transition-colors line-clamp-2"
                          style={{ fontFamily: "'Playfair Display', serif" }}
                        >
                          {post.title}
                        </h3>
                        <p className="text-sm text-gray-500 leading-relaxed line-clamp-3 mb-4">{post.excerpt}</p>
                        <a
                          href={`/blog/${post.slug}`}
                          className="inline-flex items-center justify-center px-6 py-3 bg-action-orange-500 text-white font-semibold rounded-lg hover:bg-action-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
                        >
                          Continuar Leitura
                          <i className="ri-arrow-right-line ml-2"></i>
                        </a>
                      </div>
                    </article>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

        {/* CTA Banner */}
        <section className="py-16 bg-stone-900">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h3
              className="text-2xl lg:text-3xl font-bold text-white mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Pronto para proteger seu patrimônio com inteligência?
            </h3>
            <p className="text-sm text-gray-300 mb-8">
              Solicite uma análise gratuita de riscos e descubra quanto você pode economizar.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a
                href="https://wa.me/5577988080505?text=Ol%C3%A1%2C%20gostaria%20de%20solicitar%20uma%20an%C3%A1lise%20de%20riscos."
                target="_blank"
                rel="nofollow noreferrer"
                className="inline-flex items-center gap-2 bg-green-500 text-white px-7 py-3.5 rounded-lg text-sm font-semibold hover:bg-green-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-base"></i>
                Falar no WhatsApp
              </a>
              <a
                href="/#contato"
                className="inline-flex items-center gap-2 bg-orange-500 text-white px-7 py-3.5 rounded-lg text-sm font-semibold hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-file-search-line text-base"></i>
                Solicitar Análise Gratuita
              </a>
            </div>
          </div>
        </section>

        <BlogFooter />
      </div>
    </>
  );
}